import { useState, RefObject, useCallback, useLayoutEffect } from 'react'

import useWindowDimensions from '~/hooks/useWindowDimensions'

type Position = {
  top?: number
  left?: number
  width?: number
  alignment?: 'top' | 'bottom'
  connectorsTop?: number
  connectorsLeft?: number
}

type UseMouseOverProps = {
  elementRef: RefObject<HTMLElement>
  tooltipRef: RefObject<HTMLDivElement>
}

export function useMouseOver({ elementRef, tooltipRef }: UseMouseOverProps) {
  const [isVisible, setIsVisible] = useState<boolean>(false)

  const [position, setPosition] = useState<Position>({})

  const openCard = useCallback(() => setIsVisible(true), [setIsVisible])

  const closeCard = useCallback(() => setIsVisible(false), [setIsVisible])

  const windowDimensions = useWindowDimensions()

  const calcPositions = useCallback(() => {
    if (!elementRef.current) {
      return
    }

    if (isVisible) {
      const { left, width, bottom, top } =
        elementRef.current.getBoundingClientRect()

      const tooltipWidth =
        tooltipRef.current?.getBoundingClientRect().width || 0

      const tooltipHeight =
        tooltipRef.current?.getBoundingClientRect().height || 0

      const windowHeight = window.innerHeight

      const isBottomReached = bottom + tooltipHeight >= windowHeight

      const middle = left + width / 2 - tooltipWidth / 2

      const verticalOffset = 12

      const topPosition = isBottomReached
        ? top - verticalOffset - tooltipHeight
        : bottom + verticalOffset

      const connectorsTop = isBottomReached ? tooltipHeight : -verticalOffset

      const connectorsLeft = tooltipWidth / 2

      setPosition({
        alignment: isBottomReached ? 'top' : 'bottom',
        connectorsTop,
        connectorsLeft,
        top: topPosition,
        left: middle,
        width
      })
    }

    if (!isVisible) {
      setPosition({})
    }
  }, [elementRef, isVisible, tooltipRef])

  useLayoutEffect(calcPositions, [calcPositions, windowDimensions])

  return {
    position: {
      top: position.top ?? 0,
      left: position.left ?? 0,
      width: position.width ?? 0,
      connectorsTop: position.connectorsTop,
      connectorsLeft: position.connectorsLeft,
      alignment: position.alignment
    },
    isVisible,
    openCard,
    closeCard
  }
}
